/**
 * Standalone OAuth 2.0 Authorization Code + PKCE client.
 *
 * Used when the admin UI runs **outside** the Kadanza Admin Console iframe
 * (standalone mode). In integration mode the host delivers the JWT via the
 * postMessage handshake instead, and none of this runs.
 *
 * The flow targets the same FusionAuth application that mints the extension's
 * JWT, so the resulting access token carries the extension claims
 * (`tenant_id`, `aud=ext:<slug>`, `scope`, …) the API Worker expects.
 *
 * Endpoints (FusionAuth / OIDC):
 *   authorize: `<issuer>/oauth2/authorize`
 *   token:     `<issuer>/oauth2/token`
 *
 * Configuration (Vite env, build-time):
 *   VITE_OAUTH_ISSUER        FusionAuth issuer base URL (required to enable)
 *   VITE_OAUTH_CLIENT_ID     FusionAuth application/client ID (required to enable)
 *   VITE_OAUTH_REDIRECT_URI  optional; defaults to `<origin>/`
 *   VITE_OAUTH_SCOPE         optional; defaults to `openid offline_access`
 *   VITE_OAUTH_TOKEN_PROXY   optional; route the token exchange through the API
 *                            instead of calling the IdP directly (avoids IdP
 *                            CORS). `true`/`1` → `<VITE_ADMIN_API_URL>/oauth/token`,
 *                            or set a full URL.
 */

import { resolveBaseUrl } from '@/api/client.js';

const STORAGE_PREFIX = 'grafx-vdp.oauth';
const VERIFIER_KEY = `${STORAGE_PREFIX}.verifier`;
const STATE_KEY = `${STORAGE_PREFIX}.state`;
const TOKEN_KEY = `${STORAGE_PREFIX}.token`;

export interface OAuthToken {
  jwt: string;
  /** Unix seconds (string) — matches the handshake `AuthToken.expires` format. */
  expires: string;
  refreshToken: string | null;
}

interface OAuthConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

function trimSlash(value: string): string {
  return value.replace(/\/$/, '');
}

/** Resolve config from env. Returns null when OAuth isn't configured. */
export function getOAuthConfig(): OAuthConfig | null {
  const env = import.meta.env;
  const issuer = env.VITE_OAUTH_ISSUER?.trim();
  const clientId = env.VITE_OAUTH_CLIENT_ID?.trim();
  if (!issuer || !clientId) return null;

  const redirectUri =
    env.VITE_OAUTH_REDIRECT_URI?.trim() ||
    (typeof window !== 'undefined' ? `${window.location.origin}/` : '');
  const scope = env.VITE_OAUTH_SCOPE?.trim() || 'openid offline_access';

  return { issuer: trimSlash(issuer), clientId, redirectUri, scope };
}

export function isOAuthConfigured(): boolean {
  return getOAuthConfig() !== null;
}

/**
 * Resolve the token endpoint. When `VITE_OAUTH_TOKEN_PROXY` is set, the request
 * goes through the API's server-to-server proxy (`/oauth/token`) so the browser
 * never hits the IdP token endpoint cross-origin; otherwise it calls the IdP
 * directly (which requires IdP CORS to allow this origin).
 */
function resolveTokenEndpoint(config: OAuthConfig): string {
  const proxy = import.meta.env.VITE_OAUTH_TOKEN_PROXY?.trim();
  if (proxy) {
    if (proxy === 'true' || proxy === '1') return `${resolveBaseUrl()}/oauth/token`;
    return proxy.replace(/\/$/, '');
  }
  return `${config.issuer}/oauth2/token`;
}

/** The redirect URI the app will use — surface it so it can be registered in FusionAuth. */
export function getRedirectUri(): string | null {
  return getOAuthConfig()?.redirectUri ?? null;
}

// ── PKCE helpers ────────────────────────────────────────────────────────────

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

// ── Token storage ───────────────────────────────────────────────────────────

function parseExpiresIn(expiresIn: unknown): string {
  const seconds = typeof expiresIn === 'number' && expiresIn > 0 ? expiresIn : 3600;
  return Math.floor(Date.now() / 1000 + seconds).toString();
}

export function getStoredToken(): OAuthToken | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OAuthToken;
    if (!parsed.jwt || !parsed.expires) return null;
    // Treat tokens within 30s of expiry as stale so we proactively refresh.
    if (Number(parsed.expires) * 1000 - Date.now() < 30_000) return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeToken(token: OAuthToken): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(token));
}

export function clearStoredToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

// ── Flow ────────────────────────────────────────────────────────────────────

/**
 * Redirect the browser to the authorization endpoint. Does not return (the
 * page navigates away). Stores the PKCE verifier + state for the callback.
 */
export async function beginLogin(): Promise<void> {
  const config = getOAuthConfig();
  if (!config) throw new Error('OAuth is not configured');

  const verifier = randomUrlSafe(64);
  const state = randomUrlSafe(16);
  const challenge = await sha256Challenge(verifier);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.assign(`${config.issuer}/oauth2/authorize?${params.toString()}`);
}

/**
 * End the standalone session. Clears the locally stored token plus the PKCE
 * verifier/state this module owns, then terminates the IdP session.
 *
 * When OAuth is configured the browser is redirected to the FusionAuth
 * end-session endpoint (`<issuer>/oauth2/logout`), passing `client_id` and the
 * registered `post_logout_redirect_uri` so FusionAuth returns the user to the
 * app origin (where the auth gate re-triggers a fresh sign-in). When OAuth
 * isn't configured there's no IdP session to end, so we just reload to
 * `<origin>/`. Does not return when it redirects (the page navigates away).
 */
export function logout(): void {
  clearStoredToken();
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  const config = getOAuthConfig();
  if (!config) {
    if (typeof window !== 'undefined') window.location.assign(`${window.location.origin}/`);
    return;
  }

  const params = new URLSearchParams({ client_id: config.clientId });
  if (config.redirectUri) params.set('post_logout_redirect_uri', config.redirectUri);
  window.location.assign(`${config.issuer}/oauth2/logout?${params.toString()}`);
}

/** True when the current URL looks like an OAuth redirect callback. */
export function hasAuthCodeInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const sp = new URLSearchParams(window.location.search);
  return sp.has('code') || sp.has('error');
}

async function exchangeCodeForToken(code: string): Promise<OAuthToken> {
  const config = getOAuthConfig();
  if (!config) throw new Error('OAuth is not configured');

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Missing PKCE verifier (state lost between redirects)');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: config.redirectUri,
    client_id: config.clientId,
    code_verifier: verifier,
  });

  const tokenUrl = resolveTokenEndpoint(config);
  let res: Response;
  try {
    res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
  } catch (e) {
    // "Failed to fetch" → the browser blocked the cross-origin call before it
    // could read a response. Almost always a CORS or TLS problem.
    const origin = typeof window !== 'undefined' ? window.location.origin : '<origin>';
    throw new Error(
      `Could not reach the token endpoint ${tokenUrl}. This is a CORS or TLS issue, ` +
        `not an OAuth error — set VITE_OAUTH_TOKEN_PROXY to route the exchange through ` +
        `the API, or ensure the IdP CORS config allows origin ${origin}. ` +
        `(${e instanceof Error ? e.message : String(e)})`,
    );
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Token exchange failed (${res.status}): ${detail}`);
  }
  const json = (await res.json()) as {
    access_token?: string;
    expires_in?: number;
    refresh_token?: string;
  };
  if (!json.access_token) throw new Error('Token response missing access_token');

  return {
    jwt: json.access_token,
    expires: parseExpiresIn(json.expires_in),
    refreshToken: json.refresh_token ?? null,
  };
}

/**
 * If the URL carries an OAuth callback (`?code=…&state=…`), validate state,
 * exchange the code, persist the token, and clean the URL. Returns the token,
 * or null when there's no callback to handle. Throws on a real OAuth error.
 */
export async function handleRedirectCallback(): Promise<OAuthToken | null> {
  if (!hasAuthCodeInUrl()) return null;

  const sp = new URLSearchParams(window.location.search);
  const cleanUrl = () => {
    const url = new URL(window.location.href);
    url.search = '';
    window.history.replaceState({}, document.title, url.toString());
  };

  const oauthError = sp.get('error');
  if (oauthError) {
    cleanUrl();
    throw new Error(`Authorization failed: ${sp.get('error_description') ?? oauthError}`);
  }

  const code = sp.get('code');
  const returnedState = sp.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  if (!code) return null;
  if (!returnedState || returnedState !== expectedState) {
    cleanUrl();
    throw new Error('OAuth state mismatch — possible CSRF, aborting.');
  }

  try {
    const token = await exchangeCodeForToken(code);
    storeToken(token);
    return token;
  } finally {
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    cleanUrl();
  }
}

/** Exchange a refresh token for a new access token, or return null if unavailable. */
export async function refreshAccessToken(): Promise<OAuthToken | null> {
  const config = getOAuthConfig();
  if (!config) return null;
  const current = (() => {
    try {
      const raw = sessionStorage.getItem(TOKEN_KEY);
      return raw ? (JSON.parse(raw) as OAuthToken) : null;
    } catch {
      return null;
    }
  })();
  if (!current?.refreshToken) return null;

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: current.refreshToken,
    client_id: config.clientId,
  });
  const res = await fetch(resolveTokenEndpoint(config), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  }).catch(() => null);
  if (!res || !res.ok) return null;
  const json = (await res.json()) as {
    access_token?: string;
    expires_in?: number;
    refresh_token?: string;
  };
  if (!json.access_token) return null;

  const token: OAuthToken = {
    jwt: json.access_token,
    expires: parseExpiresIn(json.expires_in),
    refreshToken: json.refresh_token ?? current.refreshToken,
  };
  storeToken(token);
  return token;
}
